import React, { useCallback, useState } from 'react';
import inventoryApi from '../../services/api/inventoryApi';
import notaEntradaMapeamentosApi from '../../services/api/notaEntradaMapeamentosApi';
import './MapeamentoProdutos.css';

const STEP_FORM = 1;   // seleção de produto + fator (tudo junto)
const STEP_REVIEW = 2;

const FATOR_PRESETS = [1, 10, 100, 1000];
const FATOR_DEFAULT = 1000;

const MapeamentoProdutos = ({ mapeamentoJson, tenantId, idCliente, onRefresh }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [modalProduto, setModalProduto] = useState(null);
  const [step, setStep] = useState(STEP_FORM);
  const [searchTerm, setSearchTerm] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [multiploQuantidade, setMultiploQuantidade] = useState(FATOR_DEFAULT);
  const [customFator, setCustomFator] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const openModal = useCallback((produto) => {
    setModalProduto(produto);
    setStep(STEP_FORM);
    setSearchTerm('');
    setSuggestions([]);
    setSelectedProduct(null);
    setMultiploQuantidade(FATOR_DEFAULT);
    setCustomFator(false);
    setSubmitError(null);
    setSubmitSuccess(false);
    setModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setModalProduto(null);
  }, []);

  const searchProducts = useCallback(async () => {
    if (!searchTerm.trim()) return;
    setSuggestionsLoading(true);
    try {
      const list = await inventoryApi.getProductSuggestions(searchTerm.trim());
      setSuggestions(Array.isArray(list) ? list : (list?.products || []));
    } catch (e) {
      setSuggestions([]);
    } finally {
      setSuggestionsLoading(false);
    }
  }, [searchTerm]);

  const handleConfirmCreate = useCallback(async () => {
    if (!modalProduto || !selectedProduct || multiploQuantidade <= 0 || !tenantId || !idCliente) return;
    setSubmitLoading(true);
    setSubmitError(null);
    try {
      await notaEntradaMapeamentosApi.criarMapeamentoProduto({
        tenantIdContext: tenantId,
        idFornecedor: idCliente,
        codProdutoFornecedor: modalProduto.cod_produto_original || '',
        codProdutoInterno: selectedProduct.codigo || selectedProduct.codigoProduto || '',
        multiploQuantidade: Number(multiploQuantidade)
      });
      setSubmitSuccess(true);
      if (onRefresh) onRefresh();
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.details || err.message || 'Erro ao criar mapeamento';
      setSubmitError(msg);
    } finally {
      setSubmitLoading(false);
    }
  }, [modalProduto, selectedProduct, multiploQuantidade, tenantId, idCliente, onRefresh]);

  if (!mapeamentoJson) {
    return (
      <div className="no-produtos">
        <p>Nenhum dado de mapeamento disponível</p>
      </div>
    );
  }

  // Parse JSON se necessário
  let data;
  try {
    data = typeof mapeamentoJson === 'string' ? JSON.parse(mapeamentoJson) : mapeamentoJson;
  } catch (e) {
    return (
      <div className="json-error">
        Erro ao processar dados do mapeamento: {e.message}
      </div>
    );
  }

  const produtos = data.produtos || [];
  const totalProdutos = data.total_produtos || produtos.length;
  const produtosSucesso = data.produtos_sucesso || produtos.filter(p => p.sucesso).length;
  const produtosErro = data.produtos_erro || produtos.filter(p => !p.sucesso).length;

  const formatCurrency = (value) => {
    if (!value || value === null || value === '') return '-';
    const str = value.toString();
    // Normalize to dot as decimal separator for parsing
    const normalized = str.replace(',', '.');
    const num = parseFloat(normalized);
    if (isNaN(num)) return str;
    // Preserve all decimal places from the original string
    const decimalPlaces = Math.max(
      (str.split('.')[1] || '').length,
      (str.split(',')[1] || '').length
    );
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: Math.max(2, decimalPlaces),
      maximumFractionDigits: Math.max(2, decimalPlaces)
    }).format(num);
  };

  const formatNumber = (value) => {
    if (!value || value === null || value === '') return '-';
    const str = value.toString();
    // Normalize to dot as decimal separator for parsing
    const normalized = str.replace(',', '.');
    const num = parseFloat(normalized);
    if (isNaN(num)) return str;
    // Preserve all decimal places from the original string
    const decimalPlaces = Math.max(
      (str.split('.')[1] || '').length,
      (str.split(',')[1] || '').length
    );
    return new Intl.NumberFormat('pt-BR', {
      minimumFractionDigits: decimalPlaces,
      maximumFractionDigits: decimalPlaces
    }).format(num);
  };

  const parseNumber = (value) => {
    if (!value || value === null || value === '') return 0;
    const str = value.toString();
    const normalized = str.replace(',', '.');
    const num = parseFloat(normalized);
    return isNaN(num) ? 0 : num;
  };

  const calculateTotal = (quantidade, valorUnitario) => {
    const qtd = parseNumber(quantidade);
    const valor = parseNumber(valorUnitario);
    return qtd * valor;
  };

  return (
    <div className="mapeamento-produtos">
      {/* Resumo */}
      <div className="produtos-resumo">
        <div className="resumo-item">
          <span className="resumo-label">Total de Produtos</span>
          <span className="resumo-value">{totalProdutos}</span>
        </div>
        <div className="resumo-item resumo-success">
          <span className="resumo-label">Mapeados com Sucesso</span>
          <span className="resumo-value">{produtosSucesso}</span>
        </div>
        <div className="resumo-item resumo-error">
          <span className="resumo-label">Com Erro</span>
          <span className="resumo-value">{produtosErro}</span>
        </div>
      </div>

      {/* Lista de Produtos */}
      <div className="produtos-lista">
        {produtos.length === 0 ? (
          <div className="no-produtos">
            <p>Nenhum produto encontrado neste mapeamento</p>
          </div>
        ) : (
          produtos.map((produto, index) => (
            <div
              key={produto.id_ped_produto || index}
              className={`produto-card ${produto.sucesso ? 'produto-success' : 'produto-error'}`}
            >
              <div className="produto-header">
                <div className="produto-status">
                  {produto.sucesso ? (
                    <span className="status-icon success-icon">✓</span>
                  ) : (
                    <span className="status-icon error-icon">✗</span>
                  )}
                  <span className="produto-status-text">
                    {produto.sucesso ? 'Mapeado com Sucesso' : 'Erro no Mapeamento'}
                  </span>
                </div>
                {produto.erro && (
                  <div className="produto-erro-message">
                    {produto.erro}
                  </div>
                )}
              </div>

              <div className="produto-content">
                {/* Produto Original */}
                <div className="produto-section produto-original">
                  <h5 className="produto-section-title">Produto Original</h5>
                  <div className="produto-info-grid">
                    <div className="produto-info-item">
                      <span className="produto-info-label">Código:</span>
                      <span className="produto-info-value">{produto.cod_produto_original || '-'}</span>
                    </div>
                    <div className="produto-info-item">
                      <span className="produto-info-label">ID:</span>
                      <span className="produto-info-value">{produto.id_produto_original || '-'}</span>
                    </div>
                    <div className="produto-info-item produto-info-full">
                      <span className="produto-info-label">Descrição:</span>
                      <span className="produto-info-value">{produto.desc_produto_original || '-'}</span>
                    </div>
                    <div className="produto-info-item">
                      <span className="produto-info-label">Quantidade:</span>
                      <span className="produto-info-value">{formatNumber(produto.qtde_original)}</span>
                    </div>
                    <div className="produto-info-item">
                      <span className="produto-info-label">Valor Unitário:</span>
                      <span className="produto-info-value">{formatCurrency(produto.valor_unit_original)}</span>
                    </div>
                    <div className="produto-info-item">
                      <span className="produto-info-label">Valor Total:</span>
                      <span className="produto-info-value">{formatCurrency(calculateTotal(produto.qtde_original, produto.valor_unit_original))}</span>
                    </div>
                  </div>
                </div>

                {/* Seta/Divisor */}
                {produto.sucesso && (
                  <div className="produto-arrow">
                    <span>→</span>
                  </div>
                )}

                {/* Produto Mapeado */}
                {produto.sucesso ? (
                  <div className="produto-section produto-mapeado">
                    <h5 className="produto-section-title">Produto Mapeado</h5>
                    <div className="produto-info-grid">
                      <div className="produto-info-item">
                        <span className="produto-info-label">Código:</span>
                        <span className="produto-info-value">{produto.cod_produto_mapeado || '-'}</span>
                      </div>
                      <div className="produto-info-item">
                        <span className="produto-info-label">ID:</span>
                        <span className="produto-info-value">{produto.id_produto_mapeado || '-'}</span>
                      </div>
                      <div className="produto-info-item produto-info-full">
                        <span className="produto-info-label">Descrição:</span>
                        <span className="produto-info-value">{produto.desc_produto_mapeado || '-'}</span>
                      </div>
                      <div className="produto-info-item">
                        <span className="produto-info-label">Quantidade:</span>
                        <span className="produto-info-value">{formatNumber(produto.qtde_mapeada)}</span>
                      </div>
                      <div className="produto-info-item">
                        <span className="produto-info-label">Valor Unitário:</span>
                        <span className="produto-info-value">{formatCurrency(produto.valor_unit_mapeado)}</span>
                      </div>
                      <div className="produto-info-item">
                        <span className="produto-info-label">Valor Total:</span>
                        <span className="produto-info-value">{formatCurrency(calculateTotal(produto.qtde_mapeada, produto.valor_unit_mapeado))}</span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="produto-section produto-mapeado produto-no-mapping">
                    <h5 className="produto-section-title">Produto Mapeado</h5>
                    <div className="no-mapping-message">
                      <span>Nenhum produto mapeado</span>
                    </div>
                    {tenantId != null && idCliente != null && (
                      <button
                        type="button"
                        className="btn btn-primary btn-adicionar-mapeamento"
                        onClick={() => openModal(produto)}
                      >
                        Adicionar mapeamento
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Modal Adicionar mapeamento */}
      {modalOpen && modalProduto && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content modal-mapeamento-produto" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h4>Adicionar mapeamento</h4>
              <button type="button" className="modal-close" onClick={closeModal} aria-label="Fechar">&times;</button>
            </div>
            <div className="modal-body">
              {submitSuccess ? (
                <div className="mapeamento-success-message">
                  <p>Regra de mapeamento criada para todos os tenants.</p>
                  <p className="hint">Você pode rodar o mapeamento novamente para aplicar a nova regra.</p>
                  <button type="button" className="btn btn-primary" onClick={closeModal}>Fechar</button>
                </div>
              ) : (
                <>
                  {step === STEP_FORM && (
                    <div className="modal-steps-together">
                      <div className="modal-step">
                        <p className="modal-step-label">Buscar produto interno (por código ou descrição)</p>
                        <div className="search-row">
                          <input
                            type="text"
                            className="input-search"
                            placeholder="Código ou descrição"
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && searchProducts()}
                          />
                          <button type="button" className="btn btn-secondary" onClick={searchProducts} disabled={suggestionsLoading}>Buscar</button>
                        </div>
                        {suggestionsLoading && <p>Carregando...</p>}
                        {suggestions.length > 0 && (
                          <ul className="suggestions-list">
                            {suggestions.map((p, i) => {
                              const cod = p.codigo ?? p.Codigo ?? p.codigoProduto ?? '';
                              const desc = p.descricao ?? p.Descricao ?? '';
                              return (
                                <li key={cod + i}>
                                  <button
                                    type="button"
                                    className={`suggestion-item ${selectedProduct === p ? 'selected' : ''}`}
                                    onClick={() => setSelectedProduct(p)}
                                  >
                                    <strong>{cod}</strong> – {desc}
                                  </button>
                                </li>
                              );
                            })}
                          </ul>
                        )}
                        {selectedProduct && (
                          <p className="selected-info">Selecionado: {selectedProduct.codigo ?? selectedProduct.Codigo ?? selectedProduct.codigoProduto}</p>
                        )}
                      </div>
                      <div className="modal-step modal-step-fator">
                        <p className="modal-step-label">Fator de conversão (quantidade do produto interno por unidade do fornecedor)</p>
                        <div className="fator-presets">
                          {FATOR_PRESETS.map((val) => (
                            <button
                              key={val}
                              type="button"
                              className={`btn btn-fator-preset ${!customFator && Number(multiploQuantidade) === val ? 'selected' : ''}`}
                              onClick={() => { setMultiploQuantidade(val); setCustomFator(false); }}
                            >
                              {val}
                            </button>
                          ))}
                          <button
                            type="button"
                            className={`btn btn-fator-preset ${customFator ? 'selected' : ''}`}
                            onClick={() => setCustomFator(true)}
                          >
                            Outro
                          </button>
                        </div>
                        {customFator && (
                          <div className="fator-outro-row">
                            <input
                              type="number"
                              min="0.0001"
                              step="any"
                              className="input-number"
                              value={multiploQuantidade}
                              onChange={e => setMultiploQuantidade(e.target.value)}
                            />
                          </div>
                        )}
                      </div>
                      <div className="modal-step-actions">
                        <button type="button" className="btn btn-secondary" onClick={closeModal}>Cancelar</button>
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={() => setStep(STEP_REVIEW)}
                          disabled={!selectedProduct || (Number(multiploQuantidade) <= 0)}
                        >
                          Revisar
                        </button>
                      </div>
                    </div>
                  )}
                  {step === STEP_REVIEW && (
                    <div className="modal-step">
                      <p className="modal-step-label">Revisão – confirme o mapeamento</p>
                      <div className="review-box">
                        <div className="review-row">
                          <span className="review-label">Produto de origem (fornecedor):</span>
                          <span className="review-value">{modalProduto.desc_produto_original || '-'} ({modalProduto.cod_produto_original || '-'})</span>
                        </div>
                        <div className="review-row">
                          <span className="review-label">Produto mapeado (interno):</span>
                          <span className="review-value">
                            {selectedProduct?.descricao ?? selectedProduct?.Descricao ?? '-'} ({selectedProduct?.codigo ?? selectedProduct?.Codigo ?? selectedProduct?.codigoProduto ?? '-'})
                          </span>
                        </div>
                        <div className="review-row">
                          <span className="review-label">Fator de conversão:</span>
                          <span className="review-value">{multiploQuantidade}</span>
                        </div>
                      </div>
                      {submitError && <div className="modal-error">{submitError}</div>}
                      <div className="modal-step-actions">
                        <button type="button" className="btn btn-secondary" onClick={() => setStep(STEP_FORM)}>Voltar</button>
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={handleConfirmCreate}
                          disabled={submitLoading}
                        >
                          {submitLoading ? 'Criando...' : 'Confirmar e criar'}
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MapeamentoProdutos;

